<script setup>
import { ref } from 'vue'

const copyCode = async (code) => {
  try {
    await navigator.clipboard.writeText(code)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

const copied = ref(false)

const exampleCode = `{
  "name": "My Dev Container",
  "image": "mcr.microsoft.com/devcontainers/base",
  "features": {
    "ghcr.io/devcontainers/features/node:1": {}
  }
}`
</script>

<template>
  <section id="code-example" class="py-20 bg-white">
    <div class="section-container">
      <!-- Section Header -->
      <div class="text-center mb-16">
        <h2 class="text-4xl md:text-5xl font-bold text-kannister-dark-900">
          Drop-In Security. <span class="text-kannister-blue-600">Zero Breaking Changes.</span>
        </h2>
      </div>

      <!-- Code Comparison -->
      <div class="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
        <!-- Left Panel: Existing Config -->
        <div>
          <h3 class="text-lg font-semibold text-kannister-dark-700 mb-4">
            Your existing .devcontainer.json
          </h3>
          <div class="relative">
            <div class="bg-kannister-dark-900 rounded-lg p-6 overflow-x-auto">
              <pre class="text-sm text-kannister-dark-100 font-mono leading-relaxed"><code>{{ exampleCode }}</code></pre>
            </div>
            <button
              @click="copyCode(exampleCode)"
              class="absolute top-4 right-4 p-2 bg-kannister-dark-800 hover:bg-kannister-dark-700 rounded text-kannister-dark-300 transition-colors"
              :title="copied ? 'Copied!' : 'Copy code'"
            >
              <svg v-if="!copied" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </div>
          <p class="mt-3 text-sm text-kannister-dark-600 font-medium">
            Keep everything exactly as is
          </p>
        </div>

        <!-- Right Panel: With Kannister -->
        <div>
          <h3 class="text-lg font-semibold text-kannister-blue-600 mb-4">
            With Kannister (no changes needed)
          </h3>
          <div class="relative">
            <div class="bg-gradient-to-br from-kannister-blue-900 to-kannister-dark-900 rounded-lg p-6 overflow-x-auto ring-2 ring-kannister-blue-400">
              <pre class="text-sm text-kannister-dark-100 font-mono leading-relaxed"><code>{{ exampleCode }}</code></pre>
            </div>
          </div>

          <!-- Annotation Callouts -->
          <div class="mt-4 space-y-2">
            <div class="flex items-start space-x-2 text-sm">
              <svg class="w-5 h-5 text-kannister-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <span class="text-kannister-dark-700">Automatic policy enforcement</span>
            </div>
            <div class="flex items-start space-x-2 text-sm">
              <svg class="w-5 h-5 text-kannister-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <span class="text-kannister-dark-700">Real-time compliance scanning</span>
            </div>
            <div class="flex items-start space-x-2 text-sm">
              <svg class="w-5 h-5 text-kannister-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <span class="text-kannister-dark-700">Audit logging enabled</span>
            </div>
            <div class="flex items-start space-x-2 text-sm">
              <svg class="w-5 h-5 text-kannister-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <span class="text-kannister-dark-700">Zero config required</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Supporting Text -->
      <div class="mt-12 max-w-3xl mx-auto text-center">
        <p class="text-lg text-kannister-dark-600">
          Kannister works alongside your existing dev containers. <strong>No migration, no rewrites, no configuration changes.</strong> Our extensions to the .devcontainer standard add enterprise security without disrupting developer workflows.
        </p>
      </div>
    </div>
  </section>
</template>
